  import React, { useEffect, useState } from "react";
  import ModalPortal from "@/components/Global/ModalPortal";
  import { Student } from "@/components/Global/DonutChart/DonutChart";
  import "./StudentListModal.scss";
  import { useTranslation } from "react-i18next";
  import { StudentListModalPeriodRibbon } from "./StudentListModalPeriodRibbon";
  import { StudentListModalHeader } from "./StudentListModalHeader";
  import { StudentListModalHeaderRow } from "./StudentListRowHeader";
  import { StudentListRow } from "./StudentListRow";
  import AssessmentModal from "../AssessmentModal/AssessmentModal";
  import { useSelector } from "react-redux";
  import removeCircleOutline from "@/images/icons/remove-circle-outline.svg";
  import calendar from "@/images/icons/calendar-plus-icon.svg";
  import { ButtonThemed } from "@/components/shared/ButtonThemed";
  import { updateBatchAssignments } from "@/services/assignment/updateBatchAssignments";
  import { getBatchAssignments } from "@/services/assignment/getBatchAssignments";
  import { Assignment } from "@/models/Assignment";
  import { ASSIGNMENT_STATUS, ASSIGNMENT_TYPE, ENTITY_TYPE } from "@/services/assignment/constants";
  import Notifications, { makeToast, ToastTypes } from "@/components/Global/Notifications";
  import { fetchAssessmentStatusWidgetData } from "@/features/dashboard/store/slices/assessmentStatusWidgetSlice";
  import { useDispatch } from "react-redux";
  import { AppDispatch } from "@/models/AppDispatch.type";
  import useSelectedClassroom from "@/hooks/useSelectedClassroom";
  import { studentAssignmentsToTrackingStatus, TrackingStatusType } from "@/features/dashboard/utils/studentAssignmentsToTrackingStatus";
  import { AssignmentType } from "../AssessmentModal/AssessmentModal";
  import TestingWindow  from "./TestingWindow";
  import { RootState } from "@/models";
  import StudentListErrorMessage from "./StudentListErrorMessage";
  import { AssessmentWindow } from "../../utils/findAssessmentWindow";
  import { Placeholder } from "@/components/Global/Placeholder/Placeholder";
  import { useAssessmentPeriodWindows } from "@/hooks/useAssessmentPeriods";
  import { SelectedPeriodCallback } from './TestingWindow';
  import { isCheckboxDisabled } from './utils/studentModaldisableCheckBox';
  import { ScoreEntryModal } from "./ScoreEntryModal";
  import editIcon from "@/images/edit-icon.svg";
  interface SelectedWindow {
    startDate: string | null;
    endDate: string | null;
    value: number | null;
    screeningWindowType: string | null;
    assignmentId: string | null;
    assignmentType: AssignmentType | null;
    filterType: 'BENCHMARK' | 'PROGRESS_MONITOR' | 'PERIOD';
    benchmarkTag?: 'BOY' | 'MOY' | 'EOY';
  }

  interface StudentListModalProps {
    isOpen: boolean;
    onCloseHandler: () => void;
    students: Student[];
    status?: TrackingStatusType | null;
    remainingDays?: number;
    latestActivityId?: string;
    completedDate?: string;
    assessmentWindow?: {
      startDate: string;
      endDate: string;
    };
    daysUntilNextAssessmentWindowOpens?: number;
    hasUpcoming: boolean;
    hasActive: boolean;
    noAssessmentWindow?: boolean;
    districtScreeningSetup?: boolean;
    assessmentWindowStatus: AssessmentWindow;
  }

  export const StudentListModal = ({
    isOpen,
    onCloseHandler,
    students,
    remainingDays,
    daysUntilNextAssessmentWindowOpens,
    hasUpcoming,
    hasActive,
    noAssessmentWindow,
    districtScreeningSetup,
    assessmentWindowStatus
  }: StudentListModalProps) => {
    const { t: tAssessmentModal } = useTranslation("assessment_modal");
    const [selectedStudents, setSelectedStudents] = useState<{ [key: string]: Student }>({});
    const [selectAll, setSelectAll] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);
    const { districtId, schoolId } = useSelector((state: RootState) => state.userData);
    const [unassignMessage, setUnassignMessage] = useState<string | null>(null);
    const [scheduleErrorMessage, setScheduleErrorMessage] = useState<string | null>(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const [preserveSelections, setPreserveSelections] = useState(false)
    const calendarData = useAssessmentPeriodWindows();
    const currentDistrictWindow = calendarData.assessmentPeriodWindows?.find(period => {
      if (!(period.screeningWindowType === 'DistrictAssignment' || period.screeningWindowType === 'SchoolAssignment')) {
        return false;
      }
      
      // Use the same logic as findAssessmentWindow - check if today falls within the window
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      
      const [startYear, startMonth, startDay] = period.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = period.endDate.split('-').map(Number);
      const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
      const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
      start.setUTCHours(0, 0, 0, 0);
      end.setUTCHours(23, 59, 59, 999);
      
      return today >= start && today <= end;
    });
    
    const initialSelectedWindow = {
      startDate: currentDistrictWindow?.startDate || null,
      endDate: currentDistrictWindow?.endDate || null,
      value: currentDistrictWindow?.periodId || null,
      screeningWindowType: currentDistrictWindow?.screeningWindowType || null,
      assignmentId: currentDistrictWindow?.assignmentId || null,
      assignmentType: currentDistrictWindow?.assignmentType as AssignmentType | null,
      filterType: (() => {
        const type = currentDistrictWindow?.screeningWindowType;
        const assignment = currentDistrictWindow?.assignmentType;
    
        if (type === 'DistrictAssignment' || type === 'SchoolAssignment') {
          if (assignment === 'BENCHMARK') return 'BENCHMARK' as const;
          if (assignment === 'PROGRESS_MONITORING') return 'PROGRESS_MONITOR' as const;
        }
        return 'PERIOD' as const;
      })(),
      benchmarkTag: (() => {
        const tagsArray: string[] = Array.isArray(currentDistrictWindow?.tags)
          ? currentDistrictWindow.tags
          : currentDistrictWindow?.tags ? [currentDistrictWindow.tags] : [];
        if (tagsArray.includes('BOY') || tagsArray.includes('WINDOW_BOY')) return 'BOY' as const;
        if (tagsArray.includes('MOY') || tagsArray.includes('WINDOW_MOY')) return 'MOY' as const;
        if (tagsArray.includes('EOY') || tagsArray.includes('WINDOW_EOY')) return 'EOY' as const;
        return undefined;
      })()
    };
    
    const [selectedWindow, setSelectedWindow] = useState<SelectedWindow>(initialSelectedWindow);
    const [studentAssessmentStatus, setStudentAssessmentStatus] = useState<Student[]>([]);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState<boolean>(true);
    const fullStudentRoster = useSelector((state: RootState) => state.reports.roster.data.student);
    const { classroomId } = useSelectedClassroom();
    const dispatch = useDispatch<AppDispatch>();
    const [isScoreEntryModalOpen, setIsScoreEntryModalOpen] = useState(false);

    const isCurrentDistrictWindow = assessmentWindowStatus.activeAssessmentWindow && 
                                   assessmentWindowStatus.currentPeriodIdentifier === selectedWindow.value && 
                                   (selectedWindow.screeningWindowType === 'DistrictAssignment' || selectedWindow.screeningWindowType === 'SchoolAssignment') &&
                                   assessmentWindowStatus.assessmentWindowType === selectedWindow.assignmentType;
    const isCurrentOutOfDistrictWindow = !assessmentWindowStatus.activeAssessmentWindow && selectedWindow.value === assessmentWindowStatus.currentPeriodIdentifier;


    const previousDistrictWindow = React.useMemo(() => {
      if (!calendarData.assessmentPeriodWindows || selectedWindow.value === null || selectedWindow.value === undefined) {
        return false;
      }

      const selectedPeriod = calendarData.assessmentPeriodWindows.find(period =>
        period.periodId === selectedWindow.value &&
        period.screeningWindowType === selectedWindow.screeningWindowType
      );

      // Find the current district window using the same logic as above
      const currentPeriod = calendarData.assessmentPeriodWindows.find(period => {
        if (!(period.screeningWindowType === 'DistrictAssignment' || period.screeningWindowType === 'SchoolAssignment')) {
          return false;
        }
        
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        
        const [startYear, startMonth, startDay] = period.startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = period.endDate.split('-').map(Number);
        const start = new Date(Date.UTC(startYear, startMonth - 1, startDay));
        const end = new Date(Date.UTC(endYear, endMonth - 1, endDay));
        start.setUTCHours(0, 0, 0, 0);
        end.setUTCHours(23, 59, 59, 999);
        
        return today >= start && today <= end;
      });

      if (selectedPeriod?.screeningWindowType === 'DistrictAssignment' || selectedPeriod?.screeningWindowType === 'SchoolAssignment') {
        if (currentPeriod &&
            currentPeriod.periodId !== null &&
            currentPeriod.periodId !== undefined &&
            selectedWindow.value !== null &&
            selectedWindow.value !== undefined &&
            selectedWindow.value < currentPeriod.periodId) {
          return true;
        }

        if (!currentPeriod && selectedPeriod.endDate) {
          const [year, month, day] = selectedPeriod.endDate.split('-').map(Number);
          const selectedEndDate = new Date(Date.UTC(year, month - 1, day));
          selectedEndDate.setUTCHours(23, 59, 59, 999);
          const currentDate = new Date();
          return selectedEndDate < currentDate;
        }
      }

      return false;
    }, [calendarData.assessmentPeriodWindows, selectedWindow.value, selectedWindow.screeningWindowType]);

    const handleSelectedPeriodChange = (selectedOption: SelectedPeriodCallback) => {
      setSelectedWindow({
        startDate: selectedOption.startDate,
        endDate: selectedOption.endDate,
        value: selectedOption.value,
        screeningWindowType: selectedOption.screeningWindowType,
        assignmentId: selectedOption.assignmentId,
        assignmentType: selectedOption.assignmentType as AssignmentType,
        filterType: selectedOption.filterType,
        benchmarkTag: selectedOption.benchmarkTag
      });
      if (!isScheduling && !preserveSelections) {
        setSelectedStudents({});
        setSelectAll(false);
      }
    };

    const entityIdList = fullStudentRoster.map((student: any) => student.id);

    useEffect(() => {

      if (
        selectedWindow?.value !== null &&
        selectedWindow?.value !== undefined &&
        fullStudentRoster.length > 0
      ) {
        const getStudentAssignments = async () => {
          setIsLoadingAssignments(true);
          try {
            let assignments;

            if (selectedWindow.filterType === 'BENCHMARK') {
              // For BOY/MOY/EOY Benchmark selections: fetch by TYPE + TAGS, NO periodId
              assignments = await getBatchAssignments({
                entityIdList,
                entityTypeFilter: ENTITY_TYPE.STUDENT,
                districtId,
                schoolId,
                assignmentType: ["BENCHMARK"],
                limit: 10,
                tags: selectedWindow.benchmarkTag ? [selectedWindow.benchmarkTag, `WINDOW_${selectedWindow.benchmarkTag}`] : undefined
                // NO periodIds for benchmark selections
              });
            } else if (selectedWindow.filterType === 'PROGRESS_MONITOR') {
              // For Period selections: fetch PROGRESS_MONITORING assignments by periodId
              assignments = await getBatchAssignments({
                entityIdList,
                entityTypeFilter: ENTITY_TYPE.STUDENT,
                districtId,
                schoolId,
                assignmentType: ["PROGRESS_MONITORING"], // More specific - only PM for periods
                limit: 10,
                periodIds: [selectedWindow.value]
                // NO tags for period selections
              });
            } else if (selectedWindow.filterType === 'PERIOD') {
              // For Period selections: fetch BOTH BENCHMARK and PROGRESS_MONITORING assignments by periodId
              assignments = await getBatchAssignments({
                entityIdList,
                entityTypeFilter: ENTITY_TYPE.STUDENT,
                districtId,
                schoolId,
                assignmentType: ["BENCHMARK", "PROGRESS_MONITORING"], // Both types for period-based assignments
                limit: 10,
                periodIds: [selectedWindow.value]
                // NO tags for period selections
              });
            }

            const filteredAssignments = assignments.map((studentData: any) => {
              if (!studentData.assignments || studentData.assignments.length === 0) {
                return studentData;
              }

              let relevantAssignments = studentData.assignments;

              if (previousDistrictWindow && selectedWindow.assignmentId) {
                relevantAssignments = relevantAssignments.filter((assignment: any) => {
                  return assignment.parentId === selectedWindow.assignmentId;
                });
              }

              relevantAssignments.sort((a: any, b: any) =>
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );

              return {
                ...studentData,
                assignments: relevantAssignments.slice(0, 1)
              };
            });

            const trackingReportData = studentAssignmentsToTrackingStatus(
              filteredAssignments,
              fullStudentRoster,
              assessmentWindowStatus,
              selectedWindow
            );

            setStudentAssessmentStatus(trackingReportData);
          } catch (error) {
            console.error("Error fetching student assignments:", error);
          } finally {
            setIsLoadingAssignments(false);
          }
        }

        getStudentAssignments();
      }
    }, [selectedWindow.value, selectedWindow.filterType, selectedWindow.benchmarkTag, selectedWindow.screeningWindowType, fullStudentRoster])

    const mergedStudents = React.useMemo(() => {
      if (!fullStudentRoster || !students) {
        return students || [];
      }

      const studentAssessmentMap = new Map(
        students.map(student => [student.studentId, student])
      );

      return fullStudentRoster.map((student: any) => {
        const studentId = student.id;

        if (studentAssessmentMap.has(studentId)) {
          return studentAssessmentMap.get(studentId);
        }

        return {
          studentId: studentId,
          firstName: student.first_name,
          lastName: student.last_name,
          assessmentStatus: TrackingStatusType.UNASSESSED,
          latestActivityId: null,
          completedDate: null
        };
      });
    }, [students, fullStudentRoster]);

    const handleScheduleAssessment = () => {
      // Filter out paper & pencil students and other ineligible students
      const paperPencilSelected = Object.values(selectedStudents).filter(student => {
        const fullStudentData = fullStudentRoster?.find((s: any) => s.id === student.studentId);
        return fullStudentData?.metadata?.assessmentEnabled === false;
      });

      const ineligibleStudents = Object.values(selectedStudents).filter(
        student => {
          const fullStudentData = fullStudentRoster?.find((s: any) => s.id === student.studentId);
          const isPaperPencil = fullStudentData?.metadata?.assessmentEnabled === false;

          return isPaperPencil ||
                 student.assessmentStatus === TrackingStatusType.IN_PROGRESS ||
                 student.assessmentStatus === TrackingStatusType.ASSIGNED ||
                 student.assessmentStatus === TrackingStatusType.DISTRICT_SCHEDULED;
        }
      );

      setUnassignMessage(null);

      if (ineligibleStudents.length > 0) {
        const message = ineligibleStudents.length >= 3
          ? tAssessmentModal('schedule_error_multiple', {
              firstName: ineligibleStudents[0].firstName,
              lastInitial: ineligibleStudents[0].lastName.charAt(0),
              count: ineligibleStudents.length - 1
            })
          : tAssessmentModal('schedule_error_single', {
              studentList: ineligibleStudents.map(student =>
                `${student.firstName} ${student.lastName.charAt(0)}.`).join(' and ')
            });

        setScheduleErrorMessage(message);

        if (ineligibleStudents.length === Object.keys(selectedStudents).length) {
          return;
        }

        const eligibleStudents = { ...selectedStudents };
        ineligibleStudents.forEach(student => {
          delete eligibleStudents[student.studentId];
        });
        setSelectedStudents(eligibleStudents);
      } else {
        setScheduleErrorMessage(null);
      }

      setIsScheduling(true);
    }

    const handleCloseScheduling = () => {
      setIsScheduling(false);
      onCloseHandler();
      setScheduleErrorMessage(null);
    };

    const handleModalClose = () => {
      onCloseHandler();

      setTimeout(() => {
        setSelectedStudents({});
        setScheduleErrorMessage(null);
        setUnassignMessage(null);
        setSelectAll(false);
        setIsScheduling(false);
      }, 0);
    };

    const handleCancelScheduling = () => {
      setIsScheduling(false);
      setScheduleErrorMessage(null);
      setPreserveSelections(true)

      setTimeout(() => setPreserveSelections(false), 100)
    };

    const studentsSelected = Object.values(selectedStudents).some((isSelected) => isSelected);

    const toggleAllStudents = () => {
      const newSelectAll = !selectAll;
      setSelectAll(newSelectAll);

      const newSelectedStudents: { [key: string]: Student } = {};
      studentAssessmentStatus.forEach((student: Student) => {
        if (newSelectAll) {
          newSelectedStudents[student.studentId] = student;
        }
      });
      setSelectedStudents(newSelectedStudents);
    };

    const toggleStudentSelection = (studentId: string) => {
      const student = studentAssessmentStatus.find((s: Student) => s.studentId === studentId);
      if (!student) return;

      const newSelectedStudents = { ...selectedStudents };
      if (selectedStudents[studentId]) {
        delete newSelectedStudents[studentId];
      } else {
        newSelectedStudents[studentId] = student;
      }
      setSelectedStudents(newSelectedStudents);

      const allSelected = mergedStudents.every((student: Student) => newSelectedStudents[student.studentId]);
      setSelectAll(allSelected);
    };

    const handleUnassign = async () => {
      try {
        setIsDisabled(true);
        const studentsWithNoAssessments = Object.values(selectedStudents).filter(student => {
          return student && (student.assessmentStatus !== TrackingStatusType.ASSIGNED && student.assessmentStatus !== TrackingStatusType.IN_PROGRESS);
        });
        if (studentsWithNoAssessments.length > 0) {
          const message = studentsWithNoAssessments.length >= 3
            ? `${studentsWithNoAssessments[0].firstName} ${studentsWithNoAssessments[0].lastName.charAt(0)}. and ${studentsWithNoAssessments.length - 1} others have no assessments to unassign.`
            : studentsWithNoAssessments.map(student => `${student.firstName} ${student.lastName.charAt(0)}.`).join(' and ') + ' have no assessments to unassign.';
          setScheduleErrorMessage(null);
          setUnassignMessage(message);
        } else {
          setUnassignMessage(null);
        }

        const getAssignments = async () => {
          let res;

          if (selectedWindow.filterType === 'BENCHMARK') {
            // For BOY/MOY/EOY Benchmark selections: fetch by TYPE + TAGS, NO periodId
            res = await getBatchAssignments({
              entityIdList: Object.keys(selectedStudents),
              entityTypeFilter: ENTITY_TYPE.STUDENT,
              districtId: districtId,
              assignmentType: [ASSIGNMENT_TYPE.BENCHMARK],
              limit: 10,
              schoolId: schoolId,
              tags: selectedWindow.benchmarkTag ? [selectedWindow.benchmarkTag, `WINDOW_${selectedWindow.benchmarkTag}`] : undefined
              // NO periodIds for benchmark selections
            });
          } else if (selectedWindow.filterType === 'PROGRESS_MONITOR') {
            // For Period selections: fetch PROGRESS_MONITORING assignments by periodId
            res = await getBatchAssignments({
              entityIdList: Object.keys(selectedStudents),
              entityTypeFilter: ENTITY_TYPE.STUDENT,
              districtId: districtId,
              assignmentType: [ASSIGNMENT_TYPE.PROGRESS_MONITORING], // More specific - only PM for periods
              limit: 10,
              schoolId: schoolId,
              periodIds: [selectedWindow.value]
              // NO tags for period selections
            });
                     } else if (selectedWindow.filterType === 'PERIOD') {
             // For Period selections: fetch BOTH BENCHMARK and PROGRESS_MONITORING assignments by periodId
             res = await getBatchAssignments({
               entityIdList: Object.keys(selectedStudents),
               entityTypeFilter: ENTITY_TYPE.STUDENT,
               districtId: districtId,
               assignmentType: [ASSIGNMENT_TYPE.BENCHMARK, ASSIGNMENT_TYPE.PROGRESS_MONITORING], // Both types for period-based assignments
               limit: 10,
               schoolId: schoolId,
               periodIds: [selectedWindow.value]
               // NO tags for period selections
             });
          }

          return res.flatMap((item: any) => {
            let relevantAssignments = item.assignments.filter((assignment: Assignment) =>
              (assignment.status === ASSIGNMENT_STATUS.ASSIGNED || assignment.status === ASSIGNMENT_STATUS.IN_PROGRESS) &&
              (assignment.assignmentType === ASSIGNMENT_TYPE.PROGRESS_MONITORING || assignment.assignmentType === ASSIGNMENT_TYPE.BENCHMARK)
            );


            if (previousDistrictWindow && selectedWindow?.assignmentId) {
              relevantAssignments = relevantAssignments.filter((assignment: any) => {
                return assignment.parentId === selectedWindow.assignmentId;
              });
            }
            return relevantAssignments;
          });
        }

        const assignments = await getAssignments();

        const unassignAssignments = async (assignments: Assignment[]) => {
          const payload = assignments.map((assignment) => ({
            entityId: assignment.entityId,
            entityType: ENTITY_TYPE.STUDENT,
            assignmentId: assignment.assignmentId,
            status: "UNASSIGNED"
          }));

          if (payload.length > 0) {
            await updateBatchAssignments(payload);
            const studentNames = Object.values(selectedStudents)
              .filter(student => assignments.some(a => a.entityId === student.studentId));

            if (studentNames.length > 0) {
              const firstStudent = studentNames[0];
              const message = studentNames.length > 1
                ? tAssessmentModal('unassigned_multiple', {
                    firstName: firstStudent.firstName,
                    lastInitial: firstStudent.lastName.charAt(0),
                    count: studentNames.length - 1
                  })
                : tAssessmentModal('unassigned_single', {
                    firstName: firstStudent.firstName,
                    lastInitial: firstStudent.lastName.charAt(0)
                  });

              makeToast(message, ToastTypes.success, true, 5000);
            }

            document.body.classList.remove('modal-open');
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) {
              backdrop.remove();
            }
            dispatch(fetchAssessmentStatusWidgetData({
              entityIdList,
              schoolId,
              districtId,
              currentPeriodId: assessmentWindowStatus.currentPeriodIdentifier!,
              entityRoster: fullStudentRoster,
              assessmentWindowStatus,
              currentWindowType: selectedWindow.screeningWindowType || 'DistrictAssignment',
              selectedWindow: {
                filterType: selectedWindow.filterType,
                benchmarkTag: selectedWindow.benchmarkTag,
                value: selectedWindow.value
              }
            }));
          }
        }

        if (assignments.length > 0) {
          await unassignAssignments(assignments);
        } else {
          setIsDisabled(false);
        }
      } catch (error) {
        setIsDisabled(false);
      }
    };

    const allCheckboxesDisabled = isCheckboxDisabled({
      districtScreeningSetup,
      selectedWindow,
      isCurrentPeriod: !!isCurrentDistrictWindow,
      isCurrentOutOfDistrictPeriod: !!isCurrentOutOfDistrictWindow,
      previousDistrictWindow: !!previousDistrictWindow
    });

    // Paper & Pencil logic
    const paperPencilStudents = React.useMemo(() => {
      return studentAssessmentStatus.filter((student) => {
        const fullStudentData = fullStudentRoster?.find((s: any) => s.id === student.studentId);
        return fullStudentData?.metadata?.assessmentEnabled === false;
      });
    }, [studentAssessmentStatus, fullStudentRoster]);

    const selectedPaperPencilStudents = React.useMemo(() => {
      return Object.values(selectedStudents).filter((student) => {
        const fullStudentData = fullStudentRoster?.find((s: any) => s.id === student.studentId);
        return fullStudentData?.metadata?.assessmentEnabled === false;
      });
    }, [selectedStudents, fullStudentRoster]);

    const selectedDigitalStudents = React.useMemo(() => {
      return Object.values(selectedStudents).filter((student) => {
        const fullStudentData = fullStudentRoster?.find((s: any) => s.id === student.studentId);
        return fullStudentData?.metadata?.assessmentEnabled !== false;
      });
    }, [selectedStudents, fullStudentRoster]);

    const hasPaperPencilStudents = paperPencilStudents.length > 0;
    const canEnterScore = selectedPaperPencilStudents.length === 1;
    const canSchedule = selectedDigitalStudents.length > 0;

    const handleEnterScore = () => {
      if (selectedPaperPencilStudents.length === 1) {
        setIsScoreEntryModalOpen(true);
      }
    };

    const handleScoreEntryClose = () => {
      setIsScoreEntryModalOpen(false);
    };

    const handleScoreSubmitted = () => {
      setIsScoreEntryModalOpen(false);
      // Refresh data
      window.location.reload();
    };

    return (
      <ModalPortal
        isOpen={isOpen}
        onClose={handleModalClose}
        size="content"
        style={{ borderRadius: '1rem', backgroundColor: 'linear-gradient(148deg, #FFF 0.44%, #FDF7FF 49.19%)' }}
        overlayKeysCloseOverride={true}
      >
        {!isScheduling ? (
          <div className="student-list-modal" data-testid="student-list-modal" style={{ width: '33.25rem' }}>
            <StudentListModalHeader onCloseHandler={handleModalClose} />
            <StudentListErrorMessage unassignMessage={unassignMessage} scheduleErrorMessage={scheduleErrorMessage} />
              <TestingWindow assessmentModal={false} assessmentWindowStatus={assessmentWindowStatus} onSelectedPeriodChange={handleSelectedPeriodChange} currentDistrictWindow={currentDistrictWindow} controlledSelectedWindow={selectedWindow} />
            <StudentListModalPeriodRibbon
              studentListRibbon={true}
              remainingDays={remainingDays}
              hasUpcoming={hasUpcoming}
              hasActive={hasActive}
              daysUntilNextAssessmentWindowOpens={daysUntilNextAssessmentWindowOpens}
              noAssessmentWindow={noAssessmentWindow}
              districtScreeningSetup={districtScreeningSetup}
              />
            <div className="student-list-modal-body">
              <div className="student-list-modal-body-inner">
                <div className="student-list-container">
                  <StudentListModalHeaderRow
                    selectAll={selectAll}
                    toggleAllStudents={toggleAllStudents}
                    isCurrentPeriod={!!isCurrentDistrictWindow}
                    isCurrentOutOfDistrictPeriod={!!isCurrentOutOfDistrictWindow}
                    previousDistrictWindow={previousDistrictWindow || false}
                    districtScreeningSetup={!!calendarData.assessmentPeriodWindows?.find(
                      (window: any) => window.screeningWindowType === 'DistrictAssignment' || window.screeningWindowType === 'SchoolAssignment'
                    )}
                    selectedWindow={selectedWindow}
                  />
                  <div className="student-list-body">
                    {isLoadingAssignments ? (
                      fullStudentRoster.map((student: any) => (
                        <div key={student.id} style={{ marginBottom: '0.0625rem' }}>
                        <Placeholder key={student.id} heightInRem={4.5} animation="glow" colWidth={12} />
                        </div>
                      ))
                    ) : (
                      studentAssessmentStatus.map((student) => (
                        <StudentListRow
                          key={student.studentId}
                          student={student}
                          latestActivityId={student.latestActivityId}
                          completedDate={student.completedDate}
                          isSelected={!!selectedStudents[student.studentId]}
                          toggleSelection={toggleStudentSelection}
                          isCurrentPeriod={!!isCurrentDistrictWindow}
                          isCurrentOutOfDistrictPeriod={!!isCurrentOutOfDistrictWindow}
                          previousDistrictWindow={previousDistrictWindow || false}
                          districtScreeningSetup={!!calendarData.assessmentPeriodWindows?.find(
                            (window: any) => window.screeningWindowType === 'DistrictAssignment' || window.screeningWindowType === 'SchoolAssignment'
                          )}
                          selectedWindow={selectedWindow}
                        />
                      ))
                    )}
                  </div>
                  <div className="student-list-modal-body-inner-footer">
                    {hasPaperPencilStudents && (
                      <ButtonThemed
                        disabled={!canEnterScore || allCheckboxesDisabled}
                        style={{
                          opacity: !canEnterScore || allCheckboxesDisabled ? 0.5 : 1,
                          borderRadius: '0.5rem',
                        }}
                        variant="dark"
                        className="button-with-icon btn-sm student-list-modal-body-inner-footer-enter-score-button"
                        onClick={handleEnterScore}
                      >
                        <span className="d-flex flex-nowrap align-items-center">
                          <img src={editIcon} alt="Edit" />
                          Enter Score
                        </span>
                      </ButtonThemed>
                    )}

                    <ButtonThemed
                      disabled={!studentsSelected || allCheckboxesDisabled}
                      style={{
                        opacity: !studentsSelected || allCheckboxesDisabled ? 0.5 : 1,
                        borderRadius: '0.5rem',
                      }}
                      variant="dark"
                      className="button-with-icon btn-sm student-list-modal-body-inner-footer-unassign-button"
                      onClick={handleUnassign}
                    >
                      <span className="d-flex flex-nowrap align-items-center">
                        <img src={removeCircleOutline} role="presentation" onClick={handleUnassign}/>
                        Unassign
                      </span>
                    </ButtonThemed>

                    <ButtonThemed
                      disabled={!canSchedule || allCheckboxesDisabled}
                      style={{
                        opacity: !canSchedule || allCheckboxesDisabled ? 0.5 : 1,
                        borderRadius: '0.5rem',
                      }}
                      variant="dark"
                      className="button-with-icon btn-sm student-list-modal-body-inner-footer-assign-button"
                      onClick={handleScheduleAssessment}
                    >
                      <span className="d-flex flex-nowrap align-items-center">
                        <img src={calendar} role="presentation" />
                        Schedule
                      </span>
                    </ButtonThemed>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {isScheduling && (
          <div className="scheduling-content" style={{ width: '58.8125rem', borderRadius: '1rem' }}>
            <AssessmentModal
              selectedStudents={selectedStudents}
              onCloseHandler={handleCloseScheduling}
              districtId={districtId}
              schoolId={schoolId}
              scheduleErrorMessage={scheduleErrorMessage}
              classroomId={classroomId}
              handleCancelScheduling={handleCancelScheduling}
              hasActive={hasActive!}
              assessmentWindowStatus={assessmentWindowStatus}
              currentDistrictWindow={currentDistrictWindow}
              controlledSelectedWindow={selectedWindow}
              onSelectedPeriodChange={handleSelectedPeriodChange}
              previousDistrictWindow={previousDistrictWindow}
            />
          </div>
        )}
                    <Notifications />

           {/* Score Entry Modal for Paper & Pencil students */}
           {selectedPaperPencilStudents.length === 1 && (
             <ScoreEntryModal
               isOpen={isScoreEntryModalOpen}
               onClose={handleScoreEntryClose}
               student={{
                 studentId: selectedPaperPencilStudents[0].studentId,
                 firstName: selectedPaperPencilStudents[0].firstName,
                 lastName: selectedPaperPencilStudents[0].lastName,
               }}
               assignmentId={selectedWindow?.assignmentId}
               districtId={districtId}
               schoolId={schoolId}
               onScoreSubmitted={handleScoreSubmitted}
               selectedWindow={selectedWindow}
             />
           )}
      </ModalPortal>
    );
  };
